/** @jsxRuntime classic */
/** @jsx jsx */

import { useCallback, useEffect, useRef, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Colors, Icon, Typography } from '@zapier/design-system';

import { UNIVERSAL_TOPBAR_HEIGHT } from '@zapier/universal-layout';
import {
  BREAKPOINTS,
  COLOR_KEYS,
  MEDIA_MAX_WIDTH,
  MEDIA_MIN_WIDTH,
} from '../../../constants';
import { isBrowser } from '../../../utils';
import { LottieAnimation, LottieAnimationMethods } from '../../LottieAnimation';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';
import { RichTextHTMLString } from '../types';
import { SplitColorAccordionSectionProps } from './SplitColorAccordionSection';
import { Video } from '../../../components/Video/Video';

export interface AccordionItemProps {
  animationAssetsUrl?: string;
  /**
   * animationDataUrl can be a Lottie json or an mp4 file.
   */
  animationDataUrl?: string;
  animationShouldLoop?: boolean;
  animationSpeed?: number;
  body: RichTextHTMLString;
  heading: string;
  id: string;
  imageUrl?: string;
  isActive: boolean;
  mediaAltText: string;
  mediaBackgroundColor: SplitColorAccordionSectionProps['backgroundColor'];
  onClick?: () => void;
  sectionIsIntersecting: boolean;
}

const Styles = labelStyles('AccordionItem', {
  root: css({
    borderBottom: `1px solid ${Colors.neutral800}`,
  }),

  button: css({
    all: 'unset',
    width: '100%',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    color: Colors.neutral800,
    transition: 'all 250ms',

    '&:not(:disabled)': {
      cursor: 'pointer',
    },

    '@media(hover:hover)': {
      '&:hover:not(:disabled)': {
        color: '#7B7977',
      },
    },

    '&:focus-visible:not(:disabled)': {
      '&::before': {
        content: '""',
        position: 'absolute',
        border: `2px solid ${Colors.neutral800}`,
        borderRadius: '3px',
        inset: '-2px -10px',
      },
    },
  }),

  heading: css({
    ...Typography.subHeader3,
    ...Typography.heading,
    fontWeight: 500,
  }),

  content: css({
    maxHeight: 0,
    overflowY: 'hidden',
    transition: 'max-height 250ms cubic-bezier(0.25, 1, 0.5, 1)',
  }),

  body: css({
    ...Typography.paragraph3,
    paddingBottom: '20px',
    color: Colors.neutral800,

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.paragraph1Short,
    },
  }),

  mediaContainer: (
    mediaBackgroundColor: AccordionItemProps['mediaBackgroundColor'],
  ) =>
    css({
      padding: '32px 24px',
      backgroundColor: Colors[mediaBackgroundColor],
      marginBottom: '20px',

      [MEDIA_MIN_WIDTH.small]: {
        height: '100%',
        width: '55%',
        position: 'absolute',
        top: 0,
        right: 0,
        margin: 0,
        boxSizing: 'border-box',
        backgroundColor: 'transparent',
        transition: 'opacity 500ms cubic-bezier(0.25, 1, 0.5, 1)',

        '[aria-expanded="false"] + div &': {
          opacity: 0,
        },
      },

      [MEDIA_MIN_WIDTH.large]: {
        padding: '80px',
      },

      [MEDIA_MIN_WIDTH.extraLarge]: {
        padding: '96px',
      },
    }),

  media: css({
    width: '100%',
    height: '100%',
    minHeight: '100%',
    maxHeight: `calc(100vh - 192px - ${UNIVERSAL_TOPBAR_HEIGHT}px)`,
    display: 'grid',
    placeItems: 'center',
    gridTemplateRows: 'minmax(0, 1fr)',

    '& video': {
      [MEDIA_MAX_WIDTH.small]: {
        height: 'auto',
      },
    },

    '& > svg, & > img': {
      display: 'block',
      maxHeight: '100%',
      maxWidth: '100%',
    },
  }),

  icon: css({
    height: '30px',
    transition: 'transform 250ms cubic-bezier(0.25, 1, 0.5, 1)',

    '[aria-expanded="true"] &': {
      transform: 'rotate(180deg)',
    },
  }),
});

export function AccordionItem(props: AccordionItemProps) {
  const [expandedHeight, setExpandedHeight] = useState(0);

  const animationRef = useRef<LottieAnimationMethods>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const calculateExpandedHeight = useCallback(() => {
    if (contentRef.current) {
      setExpandedHeight(contentRef.current.scrollHeight);
    }
  }, []);

  const startOrStopAnimation = useCallback(() => {
    if (animationRef.current) {
      if (props.sectionIsIntersecting && props.isActive) {
        animationRef.current.play();
      } else {
        animationRef.current.stop();
      }
    }
  }, [props.sectionIsIntersecting, props.isActive]);

  useEffect(() => {
    calculateExpandedHeight();
    window.addEventListener('resize', calculateExpandedHeight);
    return () => window.removeEventListener('resize', calculateExpandedHeight);
  }, [calculateExpandedHeight]);

  useEffect(() => {
    startOrStopAnimation();
  }, [startOrStopAnimation]);

  const renderMedia = () => {
    if (props.animationDataUrl?.endsWith('.json'))
      return (
        <LottieAnimation
          animationAssetsUrl={props.animationAssetsUrl}
          animationDataUrl={props.animationDataUrl}
          ariaLabel={props.mediaAltText}
          css={Styles.media}
          loop={props.animationShouldLoop}
          onDOMLoaded={() => {
            calculateExpandedHeight();
            startOrStopAnimation();
          }}
          ref={animationRef}
          speed={props.animationSpeed}
        />
      );

    if (props.animationDataUrl && props.isActive)
      return (
        <div css={Styles.media}>
          <Video
            autoPlay={true}
            fullHeight={true}
            hideControls={true}
            loop={props.animationShouldLoop}
            onLoadedData={calculateExpandedHeight}
            title={props.mediaAltText}
            videoUrl={props.animationDataUrl}
            playbackRate={props.animationSpeed}
          />
        </div>
      );

    if (props.imageUrl)
      return (
        <div css={Styles.media}>
          <img
            alt={props.mediaAltText || ''}
            onLoad={calculateExpandedHeight}
            src={props.imageUrl}
          />
        </div>
      );

    return null;
  };

  return (
    <div css={Styles.root}>
      <button
        aria-controls={`${props.id}-content`}
        aria-expanded={props.isActive}
        css={Styles.button}
        disabled={
          props.isActive &&
          isBrowser() &&
          window.innerWidth >= BREAKPOINTS.small
        }
        id={`${props.id}-button`}
        onClick={props.onClick}
      >
        <h3 css={Styles.heading}>{props.heading}</h3>
        <div css={Styles.icon}>
          <Icon name="arrowSmallDown" />
        </div>
      </button>
      <div
        aria-hidden={!props.isActive}
        aria-labelledby={`${props.id}-button`}
        css={Styles.content}
        id={`${props.id}-content`}
        ref={contentRef}
        role="region"
        style={{ maxHeight: props.isActive ? expandedHeight : 0 }}
      >
        <div css={Styles.body}>
          <RichTextHtml
            backgroundColor={COLOR_KEYS.neutral100}
            html={props.body}
          />
        </div>
        <div css={Styles.mediaContainer(props.mediaBackgroundColor)}>
          {renderMedia()}
        </div>
      </div>
    </div>
  );
}
